import { $t } from '@/plugins/localization';
import { Option } from './Interfaces';

const stringToEnum = <T>(enumObj: T, value: string): T[keyof T] | undefined =>
{
    return (Object.values(enumObj) as unknown as string[]).includes(value) ? value as T[keyof T] : undefined;
};

export {
    stringToEnum
};

// ----------------------------------------------------------------------------------------------------

enum PublicationEnum {
    Draft = 'Draft',
    Finished = 'Finished',
    Rejected = 'Rejected',
    Accepted = 'Accepted'
}

const YesNoAllOptions = ():any =>  ([
    { value: true, text: $t('[[[Tak]]]') },
    { value: false, text: $t('[[[Nie]]]') },
    { value: null, text: $t('[[[Wszystkie]]]') },
]);
const YesNoAllName = (value: any): string => YesNoAllOptions().find(p => p.value === value)?.text || '';

const PublicationOptions = (): Option[] => ([
    { value: PublicationEnum.Draft, text: $t('[[[Szkic]]]'), icon: 'fas fa-pen-circle text-info' },
    { value: PublicationEnum.Finished, text: $t('[[[Do akceptacji]]]'), icon: 'fas fa-clock text-warning' },
    { value: PublicationEnum.Rejected, text: $t('[[[Zwrócony]]]'), icon: 'fas fa-times-circle text-danger' },
    { value: PublicationEnum.Accepted, text: $t('[[[Zaakceptowany]]]'), icon: 'fas fa-check-circle text-success' }
]);

const AllPublicationOptions = (): Option[] => (
    [ { value: null, text: $t('[[[Wszystkie]]]') } ].concat(PublicationOptions())
);

const PublicationName = (value: PublicationEnum): string => PublicationOptions().find(p => p.value === value)?.text || PublicationEnum[value];
const PublicationIcon = (value: PublicationEnum): string => PublicationOptions().find(p => p.value === value)?.icon || '';
const PublicationStyle = (classTemplate: string, status: PublicationEnum): Record<string, boolean> =>
{
    const classes: Record<string, boolean> = {};
    const re = new RegExp('\\{0\\}', 'g');

    classes[classTemplate.replace(re, 'info')] = status === PublicationEnum.Draft;
    classes[classTemplate.replace(re, 'warning')] = status === PublicationEnum.Finished;
    classes[classTemplate.replace(re, 'danger')] = status === PublicationEnum.Rejected;
    classes[classTemplate.replace(re, 'success')] = status === PublicationEnum.Accepted;

    return classes;
};

export {
    PublicationEnum,
    PublicationOptions,
    PublicationName,
    PublicationStyle,
    PublicationIcon,
    AllPublicationOptions,
    YesNoAllOptions,
    YesNoAllName,
};
// ----------------------------------------------------------------------------------------------------

enum PopupEnum{
    Big = 'Big',
    Small = 'Small',
}

const PopupOptions = ():any =>  ([
    { value: PopupEnum.Big, text: $t('[[[Duży]]]') },
    { value: PopupEnum.Small, text: $t('[[[Mały]]]') },
]);

export {
    PopupEnum,
    PopupOptions
};

// ----------------------------------------------------------------------------------------------------

enum EntityState {
    Deleted = 'Deleted',
    Modified = 'Modified',
    Added = 'Added',
    Trashed = 'Trashed',
    Recovered = 'Recovered'
}

const EntityStateOptions = (): Option[] => ([
    { value: EntityState.Deleted, text: $t('[[[Usunięcie]]]') },
    { value: EntityState.Modified, text: $t('[[[Modyfikacja]]]') },
    { value: EntityState.Added, text: $t('[[[Dodanie]]]') },
    { value: EntityState.Trashed, text: $t('[[[Kosz]]]') },
    { value: EntityState.Recovered, text: $t('[[[Przywrócenie]]]') }
]);

const EntityStateName = (value: EntityState): string => EntityStateOptions().find(p => p.value === value)?.text || EntityState[value];

export {
    EntityState,
    EntityStateOptions,
    EntityStateName
};

// ----------------------------------------------------------------------------------------------------

enum AccountType {
    Local = 'Local',
    Ldap = 'Ldap'
}

export {
    AccountType
};

// ----------------------------------------------------------------------------------------------------

enum DictionaryType {
    CoreUsers = 'CoreUsers',
    CoreRoles = 'CoreRoles',
    CoreTeams = 'CoreTeams',

    // CoreJobPositions = 'CoreJobPositions',
    // CoreCountries = 'CoreCountries',
    // CoreCities = 'CoreCities',
    // CoreProvinces = 'CoreProvinces',
    // ContactsEventTypes = 'ContactsEventTypes',
    // CalendarResourceCategories = 'CalendarResourceCategories'
}

const DictionaryTypeOptions = (): Option[] => ([
    { value: DictionaryType.CoreUsers, text: $t('[[[Administracja - Użytkownicy]]]') },
    { value: DictionaryType.CoreRoles, text: $t('[[[Administracja - Role]]]') },
    { value: DictionaryType.CoreTeams, text: $t('[[[Administracja - Zespoły]]]') }

    // { value: DictionaryType.CoreJobPositions, text: $t('[[[Administracja - Stanowiska pracy]]]') },
    // { value: DictionaryType.CoreCountries, text: $t('[[[Administracja - Kraje]]]') },
    // { value: DictionaryType.CoreCities, text: $t('[[[Administracja - Miasta]]]') },
    // { value: DictionaryType.CoreProvinces, text: $t('[[[Administracja - Województwa]]]') },
    // { value: DictionaryType.ContactsEventTypes, text: $t('[[[Kontakty - Typ działania]]]') },
    // { value: DictionaryType.CalendarResourceCategories, text: $t('[[[Kalendarz - Kategorie zasobów]]]') }
]);

const DictionaryTypeName = (value: DictionaryType): string => DictionaryTypeOptions().find(p => p.value === value)?.text || DictionaryType[value];

export {
    DictionaryType,
    DictionaryTypeOptions,
    DictionaryTypeName
};

// ----------------------------------------------------------------------------------------------------

enum QueueStage {
    SENT = 100,
    QUEUED = 200,
    DELAYED = 300,
    REPEAT = 400,
    REJECTED = 500,
    PROCESSING = 600,
    RETRY = 700,
    SUCCEEDED = 800,
    FAILED = 900
}

const QueueStageOptions = (): Option<QueueStage>[] => ([
    { value: QueueStage.SENT, text: $t('[[[Wysłane]]]') },
    { value: QueueStage.QUEUED, text: $t('[[[Zakolejkowane]]]'), icon: 'fad fa-inbox-in text-info' },
    { value: QueueStage.DELAYED, text: $t('[[[Zaplanowane]]]'), icon: 'fad fa-clock text-info' },
    { value: QueueStage.REPEAT, text: $t('[[[Powtórzone]]]') },
    { value: QueueStage.REJECTED, text: $t('[[[Odrzucone]]]') },
    { value: QueueStage.PROCESSING, text: $t('[[[Przetwarzane]]]'), icon: 'fad fa-gears text-warning' },
    { value: QueueStage.RETRY, text: $t('[[[Powtórzone]]]') },
    { value: QueueStage.SUCCEEDED, text: $t('[[[Zakończone]]]'), icon: 'fad fa-check text-success' },
    { value: QueueStage.FAILED, text: $t('[[[Błędne]]]'), icon: 'fad fa-triangle-exclamation text-danger' }
]);

const QueueStageName = (value: QueueStage): string => QueueStageOptions().find(p => p.value === value)?.text || QueueStage[value];

export {
    QueueStage,
    QueueStageOptions,
    QueueStageName
};

// ----------------------------------------------------------------------------------------------------

enum JobStage {
    QUEUED = 210,
    PROCESSING = 610,
    SUCCEEDED = 620,
    FAILED = 630
}

const JobStageOptions = (): Option<JobStage>[] => ([
    { value: JobStage.QUEUED, text: $t('[[[Zakolejkowane]]]'), icon: 'fad fa-inbox-in text-info' },
    { value: JobStage.PROCESSING, text: $t('[[[Przetwarzane]]]'), icon: 'fad fa-gears text-warning' },
    { value: JobStage.SUCCEEDED, text: $t('[[[Zakończone]]]'), icon: 'fad fa-check text-success' },
    { value: JobStage.FAILED, text: $t('[[[Błędne]]]'), icon: 'fad fa-triangle-exclamation text-danger' }
]);

const JobStageName = (value: JobStage): string => JobStageOptions().find(p => p.value === value)?.text || JobStage[value];

export {
    JobStage,
    JobStageOptions,
    JobStageName
};

// ----------------------------------------------------------------------------------------------------

enum VideoTypeEnum {
    Video = 'Video',
    Url = 'Url'
}

const VideoTypeOptions = (): Option[] => ([
    { value: VideoTypeEnum.Video, text: $t('[[[Plik wideo]]]') },
    { value: VideoTypeEnum.Url, text: $t('[[[Adres url]]]') }
]);

const VideoTypeName = (value: VideoTypeEnum): string => VideoTypeOptions().find(p => p.value === value)?.text || VideoTypeEnum[value];

export {
    VideoTypeEnum,
    VideoTypeOptions,
    VideoTypeName
};

// ----------------------------------------------------------------------------------------------------

enum BannerGroupTypeEnum {
    Internal = 'Internal',
    External = 'External'
}

const BannerGroupTypeOption = (): Option[] => ([
    { value: BannerGroupTypeEnum.Internal, text: $t('[[[Wewnętrzny]]]') },
    { value: BannerGroupTypeEnum.External, text: $t('[[[Zewnętrzny]]]') }
]);

const BannerGroupTypeName = (value: BannerGroupTypeEnum): string => BannerGroupTypeOption().find(p => p.value === value)?.text || BannerGroupTypeEnum[value];

export {
    BannerGroupTypeEnum,
    BannerGroupTypeOption,
    BannerGroupTypeName
};


// ----------------------------------------------------------------------------------------------------

enum PageModeEnum {
    Page = 'Page',
    Home = 'Home',
    Auth = 'Auth',
    Alias = 'Alias',
    Link = 'Link',
    Error404 = 'Error404',
    LandingPage = 'LandingPage'
}

const PageModeOptions = (): Option[] => ([
    { value: PageModeEnum.Page, text: $t('[[[Standardowa strona]]]') },
    { value: PageModeEnum.Home, text: $t('[[[Strona główna]]]') },
    { value: PageModeEnum.Auth, text: $t('[[[Strona logowania]]]') },
    { value: PageModeEnum.Error404, text: $t('[[[Strona z błędem 404]]]') },
    { value: PageModeEnum.Alias, text: $t('[[[Link do wybranej strony serwisu]]]') },
    { value: PageModeEnum.Link, text: $t('[[[Link do zewnętrznej strony]]]') },
    { value: PageModeEnum.LandingPage, text: $t('[[[Strona bez modułu]]]') }
]);

const PageModeName = (value: PageModeEnum): string => PageModeOptions().find(p => p.value === value)?.text || PageModeEnum[value];

export {
    PageModeEnum,
    PageModeOptions,
    PageModeName
};

// ----------------------------------------------------------------------------------------------------

enum RegulationType {
    Email = 'Email',
    Sms = 'Sms',
    Push = 'Push'
}

const RegulationTypeOptions = (): Option[] => ([
    { value: RegulationType.Email, text: $t('[[[Email]]]') },
    { value: RegulationType.Sms, text: $t('[[[Sms]]]') },
    { value: RegulationType.Push, text: $t('[[[Push]]]') }
]);


const RegulationTypeName = (value: RegulationType): string => RegulationTypeOptions().find(p => p.value === value)?.text || RegulationType[value];

export {
    RegulationType,
    RegulationTypeOptions,
    RegulationTypeName
};

// ----------------------------------------------------------------------------------------------------

enum RegulationSetType {
    AccountRegistration = 'AccountRegistration',
    Subscription = 'Subscription',
    NonStandard = 'NonStandard'
}

const RegulationSetTypeOptions = (): Option[] => ([
    { value: RegulationSetType.AccountRegistration, text: $t('[[[Rejestracja konta]]]') },
    { value: RegulationSetType.Subscription, text: $t('[[[Subskrypcja]]]') },
    { value: RegulationSetType.NonStandard, text: $t('[[[Zestaw niestandardowy]]]') }
]);

const AllRegulationSetTypeOptions = (): Option[] => (
    [ { value: null, text: $t('[[[Wszystkie]]]') } ].concat(RegulationSetTypeOptions())
);

const RegulationSetTypeName = (value: RegulationSetType): string => RegulationSetTypeOptions().find(p => p.value === value)?.text || RegulationSetType[value];

export {
    RegulationSetType,
    RegulationSetTypeOptions,
    AllRegulationSetTypeOptions,
    RegulationSetTypeName
};

// ----------------------------------------------------------------------------------------------------

enum RegulationOperationType {
    Accepted = 'Accepted',
    Rejected = 'Rejected',
    Other = 'Other',
}

const RegulationOperationTypeOptions = (): Option[] => ([
    { value: RegulationOperationType.Accepted, text: $t('[[[Rejestracja konta]]]') },
    { value: RegulationOperationType.Rejected, text: $t('[[[Subskrypcja]]]') }
]);

const RegulationOperationTypeName = (value: RegulationOperationType): string => RegulationSetTypeOptions().find(p => p.value === value)?.text || RegulationOperationType[value];

export {
    RegulationOperationType,
    RegulationOperationTypeOptions,
    RegulationOperationTypeName
};

// ----------------------------------------------------------------------------------------------------

enum SourceUrl {
    FileRepository = 'FileRepository',
    ExternalService = 'ExternalService'
}

const SourceUrleOptions = (): Option[] => ([
    { value: SourceUrl.FileRepository, text: $t('[[[Repozytorium plików]]]') },
    { value: SourceUrl.ExternalService, text: $t('[[[Zewnętrzny serwis]]]') }
]);

const SourceUrlName = (value: SourceUrl): string => SourceUrleOptions().find(p => p.value === value)?.text || SourceUrl[value];

export {
    SourceUrl,
    SourceUrleOptions,
    SourceUrlName
};

// ----------------------------------------------------------------------------------------------------

enum CommentsStatusEnum {
    Waiting = 'Waiting',
    Accepted = 'Accepted',
    Rejected = 'Rejected'
}

const CommentsStatusOptions = (): Option[] => ([
    { value: CommentsStatusEnum.Waiting, text: $t('[[[Oczekujący na akceptację]]]') },
    { value: CommentsStatusEnum.Accepted, text: $t('[[[Zaakceptowany]]]') },
    { value: CommentsStatusEnum.Rejected, text: $t('[[[Odrzucony po zgłoszeniach]]]') }
]);

const CommentsStatusName = (value: CommentsStatusEnum): string => CommentsStatusOptions().find(p => p.value === value)?.text || CommentsStatusEnum[value];
const CommentsStatusStyle = (classTemplate: string, status: CommentsStatusEnum): Record<string, boolean> =>
{
    const classes: Record<string, boolean> = {};
    const re = new RegExp('\\{0\\}', 'g');

    classes[classTemplate.replace(re, 'warning')] = status === CommentsStatusEnum.Waiting;
    classes[classTemplate.replace(re, 'success')] = status === CommentsStatusEnum.Accepted;
    classes[classTemplate.replace(re, 'danger')] = status === CommentsStatusEnum.Rejected;

    return classes;
};

export {
    CommentsStatusEnum,
    CommentsStatusOptions,
    CommentsStatusName,
    CommentsStatusStyle
};

// ----------------------------------------------------------------------------------------------------

enum RelatedArticleSectionsEnum {
    Below = 'Below',
    Inline = 'Inline'
}

const RelatedArticleSectionsOptions = (): Option[] => ([
    { value: RelatedArticleSectionsEnum.Below, text: $t('[[[Sekcja pod artykułem]]]') },
    { value: RelatedArticleSectionsEnum.Inline, text: $t('[[[Sekcja śródtekstowa]]]') },
]);

const RelatedArticleSectionsName = (value: RelatedArticleSectionsEnum): string => RelatedArticleSectionsOptions().find(p => p.value === value)?.text || RelatedArticleSectionsEnum[value];

export {
    RelatedArticleSectionsEnum,
    RelatedArticleSectionsOptions,
    RelatedArticleSectionsName
};

// ----------------------------------------------------------------------------------------------------

enum DataImportTypeEnum {
    TaxOffices = 'TaxOffices',
    Points = 'Points',
    SalesReports = 'SalesReports',
    PromotionalCodes = 'PromotionalCodes',
    Prizes = 'Prizes',
    PitFiles = 'PitFiles'
}

const DataImportTypesOptions = (): Option[] => ([
    { value: DataImportTypeEnum.TaxOffices, text: $t('[[[Urzędy skarbowe]]]') },
    { value: DataImportTypeEnum.Points, text: $t('[[[Punkty użytkowników]]]') },
    { value: DataImportTypeEnum.SalesReports, text: $t('[[[Raporty sprzedaży]]]') },
    { value: DataImportTypeEnum.PromotionalCodes, text: $t('[[[Kody promocyjne]]]') },
    { value: DataImportTypeEnum.Prizes, text: $t('[[[Nagrody]]]') },
    { value: DataImportTypeEnum.PitFiles, text: $t('[[[Pliki PIT]]]') }
]);

const DataImportTypesName = (value: DataImportTypeEnum): string => DataImportTypesOptions().find(p => p.value === value)?.text || DataImportTypeEnum[value];

export {
    DataImportTypeEnum,
    DataImportTypesOptions,
    DataImportTypesName
};
// ----------------------------------------------------------------------------------------------------

enum PromotionTypeEnum {
    Products = 'Products',
    PointsTarget = 'PointsTarget',
    Bonuses = 'Bonuses'
}

const PromotionTypesOptions = (): Option[] => ([
    { value: PromotionTypeEnum.Products, text: $t('[[[Punkty za produkt]]]') },
    { value: PromotionTypeEnum.PointsTarget, text: $t('[[[Misja Target]]]') },
    { value: PromotionTypeEnum.Bonuses, text: $t('[[[Bonus za rozliczenie]]]') }
]);

const PromotionTypesName = (value: PromotionTypeEnum): string => PromotionTypesOptions().find(p => p.value === value)?.text || PromotionTypeEnum[value];

export {
    PromotionTypeEnum,
    PromotionTypesOptions,
    PromotionTypesName
};
// ----------------------------------------------------------------------------------------------------

enum PackagePromotionTypeEnum {
    TargetPLN = 'TargetPLN',
    TargetUnits = 'TargetUnits',
    UniqueProducts = 'UniqueProducts'
}

const PackagePromotionTypesOptions = (): Option[] => ([
    { value: PackagePromotionTypeEnum.TargetPLN, text: $t('[[[Target PLN]]]') },
    { value: PackagePromotionTypeEnum.TargetUnits, text: $t('[[[Target sztuk]]]') },
    { value: PackagePromotionTypeEnum.UniqueProducts, text: $t('[[[Liczba unikalnych produktów]]]') }
]);

const PackagePromotionTypesName = (value: PackagePromotionTypeEnum): string => PackagePromotionTypesOptions().find(p => p.value === value)?.text || PackagePromotionTypeEnum[value];

export {
    PackagePromotionTypeEnum,
    PackagePromotionTypesOptions,
    PackagePromotionTypesName
};

// ----------------------------------------------------------------------------------------------------

enum RankingConfigurationTypeEnum {
    ProductSymbol = 'ProductSymbol',
    CategoryName = 'CategoryName',
    PromotionalCodePrefix = 'PromotionalCodePrefix'
}

const RankingConfigurationTypesOptions = (): Option[] => ([
    { value: RankingConfigurationTypeEnum.ProductSymbol, text: $t('[[[Symbol produktu]]]') },
    { value: RankingConfigurationTypeEnum.CategoryName, text: $t('[[[Nazwa kategorii]]]') },
    { value: RankingConfigurationTypeEnum.PromotionalCodePrefix, text: $t('[[[Prefix kodu promocyjnego]]]') },
]);

const RankingConfigurationTypesName = (value: RankingConfigurationTypeEnum): string => RankingConfigurationTypesOptions().find(p => p.value === value)?.text || RankingConfigurationTypeEnum[value];

export {
    RankingConfigurationTypeEnum,
    RankingConfigurationTypesOptions,
    RankingConfigurationTypesName
};
// ----------------------------------------------------------------------------------------------------

enum PackageConfigurationTypeEnum {
    ProductSymbol = 'ProductSymbol',
    CategoryName = 'CategoryName',
    PromotionalCodePrefix = 'PromotionalCodePrefix'
}

const PackageConfigurationTypesOptions = (): Option[] => ([
    { value: PackageConfigurationTypeEnum.ProductSymbol, text: $t('[[[Symbol produktu]]]') },
    { value: PackageConfigurationTypeEnum.CategoryName, text: $t('[[[Nazwa kategorii]]]') },
    { value: PackageConfigurationTypeEnum.PromotionalCodePrefix, text: $t('[[[Prefix kodu promocyjnego]]]') },
]);

const PackageConfigurationTypesName = (value: PackageConfigurationTypeEnum): string => PackageConfigurationTypesOptions().find(p => p.value === value)?.text || PackageConfigurationTypeEnum[value];

export {
    PackageConfigurationTypeEnum,
    PackageConfigurationTypesOptions,
    PackageConfigurationTypesName
};
// ----------------------------------------------------------------------------------------------------

enum ExposureRegistrationStatusEnum {
    Pending = 'Pending',
    Accepted = 'Accepted',
    Denied = 'Denied',

    NoPhotos = 'NoPhotos'
}

const ExposureRegistrationStatusOptions = (): Option[] => ([
    { value: ExposureRegistrationStatusEnum.Pending, text: $t('[[[Oczekujące]]]') },
    { value: ExposureRegistrationStatusEnum.Accepted, text: $t('[[[Zaakceptowane]]]') },
    { value: ExposureRegistrationStatusEnum.Denied, text: $t('[[[Odrzucone]]]') },
    { value: ExposureRegistrationStatusEnum.NoPhotos, text: $t('[[[Brak zdjęć]]]') },
]);

const ExposureRegistrationStatusesName = (value: ExposureRegistrationStatusEnum): string => ExposureRegistrationStatusOptions().find(p => p.value === value)?.text || ExposureRegistrationStatusEnum[value];

export {
    ExposureRegistrationStatusEnum,
    ExposureRegistrationStatusOptions,
    ExposureRegistrationStatusesName
};


// ----------------------------------------------------------------------------------------------------

enum PromotionRankingTypeEnum {
    Unit = 'Unit',
    Points = 'Points',
}

const PromotionRankingTypesOptions = (): Option[] => ([
    { value: PromotionRankingTypeEnum.Unit, text: $t('[[[Po sztukach]]]') },
    { value: PromotionRankingTypeEnum.Points, text: $t('[[[Po zdobytych punktach]]]') },
]);

const PromotionRankingTypesName = (value: PromotionRankingTypeEnum): string => PromotionRankingTypesOptions().find(p => p.value === value)?.text || PromotionRankingTypeEnum[value];

export {
    PromotionRankingTypeEnum,
    PromotionRankingTypesOptions,
    PromotionRankingTypesName
};
// ----------------------------------------------------------------------------------------------------

enum UserPointTypeEnum {
    PointsBasic = 'PointsBasic',
    PointsCorrection = 'PointsCorrection',
    PointsPrize = 'PointsPrize',
    PointsCardCharge = 'PointsCardCharge',
    PointsReturn = 'PointsReturn',
    PointsQuiz = 'PointsQuiz',
    PointsPromotion = 'PointsPromotion',
    PointsSurvey = 'PointsSurvey',
    FrozenPoints = 'FrozenPoints',
    FrozenPromotionalPoints = 'FrozenPromotionalPoints'
}

const UserPointsOptions = (): Option[] => ([
    { value: UserPointTypeEnum.PointsBasic, text: $t('[[[Points - Naliczenie]]]') },
    { value: UserPointTypeEnum.PointsCorrection, text: $t('[[[Points - Korekta]]]') },
    { value: UserPointTypeEnum.PointsPrize, text: $t('[[[Points - Sklep]]]') },
    { value: UserPointTypeEnum.PointsCardCharge, text: $t('[[[Points - Wypłata bonusu]]]') },
    { value: UserPointTypeEnum.PointsReturn, text: $t('[[[Points - Zwrot]]]') },
    { value: UserPointTypeEnum.PointsQuiz, text: $t('[[[Points - Quiz]]]') },
    { value: UserPointTypeEnum.PointsPromotion, text: $t('[[[Points - Promocja]]]') },
    { value: UserPointTypeEnum.PointsSurvey, text: $t('[[[Points - Ankieta]]]') },
    { value: UserPointTypeEnum.FrozenPoints, text: $t('[[[Punkty - Zamrożone]]]') },
    { value: UserPointTypeEnum.FrozenPromotionalPoints, text: $t('[[[Punkty promocyjne - Zamrożone]]]') }
]);

const UserPointsName = (value: UserPointTypeEnum): string => UserPointsOptions().find(p => p.value === value)?.text || UserPointTypeEnum[value];

export {
    UserPointTypeEnum,
    UserPointsOptions,
    UserPointsName
};
// ----------------------------------------------------------------------------------------------------

enum SaleReportTypeEnum {
    New = 'New',
    Calculated = 'Calculated',
    Rejected = 'Rejected'
}

const SaleReportsOptions = (): Option[] => ([
    { value: SaleReportTypeEnum.New, text: $t('[[[Nowy]]]') },
    { value: SaleReportTypeEnum.Calculated, text: $t('[[[Naliczone]]]') },
    { value: SaleReportTypeEnum.Rejected, text: $t('[[[Odrzucone]]]') }
]);

const SaleReportsName = (value: SaleReportTypeEnum): string => SaleReportsOptions().find(p => p.value === value)?.text || SaleReportTypeEnum[value];

export {
    SaleReportTypeEnum,
    SaleReportsOptions,
    SaleReportsName
};
// ----------------------------------------------------------------------------------------------------

enum PrizeTypeEnum {
    PrepaidCardCharge = 'PrepaidCardCharge',
    Blik = 'Blik',
    MaterialPrize = 'MaterialPrize'
}

const PrizeTypeOptions = (): Option[] => ([
    { value: PrizeTypeEnum.PrepaidCardCharge, text: $t('[[[Doładowanie VISA]]]') },
    { value: PrizeTypeEnum.MaterialPrize, text: $t('[[[Nagroda rzeczowa]]]') },
    { value: PrizeTypeEnum.Blik, text: $t('[[[BLIK]]]') }
]);

const PrizeTypeName = (value: PrizeTypeEnum): string => PrizeTypeOptions().find(p => p.value === value)?.text || PrizeTypeEnum[value];

export {
    PrizeTypeEnum,
    PrizeTypeOptions,
    PrizeTypeName
};
// ----------------------------------------------------------------------------------------------------

enum QuizQuestionTypeEnum {
    Theory = 'Theory',
    SingleChoice = 'SingleChoice',
    MultiChoice = 'MultiChoice',
    FileUpload = 'FileUpload',
    TrueFalse = 'TrueFalse',
    Scale = 'Scale'
}

const QuizQuestionsOptions = (): Option[] => ([
    { value: QuizQuestionTypeEnum.Theory, text: $t('[[[Text]]]') },
    { value: QuizQuestionTypeEnum.SingleChoice, text: $t('[[[Pojedynczy wybór]]]') },
    { value: QuizQuestionTypeEnum.MultiChoice, text: $t('[[[Wielokrotny wybór]]]') },
    { value: QuizQuestionTypeEnum.FileUpload, text: $t('[[[Upload pliku]]]') },
    { value: QuizQuestionTypeEnum.TrueFalse, text: $t('[[[Tak/Nie]]]') },
    { value: QuizQuestionTypeEnum.Scale, text: $t('[[[Skala]]]') },
]);

const QuizQuestionsName = (value: QuizQuestionTypeEnum): string => QuizQuestionsOptions().find(p => p.value === value)?.text || QuizQuestionTypeEnum[value];

export {
    QuizQuestionTypeEnum,
    QuizQuestionsOptions,
    QuizQuestionsName
};

// ----------------------------------------------------------------------------------------------------

enum PackageTypeEnum {
    Premium = 'Premium',
    Articles = 'Articles'
}

const PackageTypeOptions = (): Option[] => ([
    { value: PackageTypeEnum.Premium, text: $t('[[[Premium]]]') },
    { value: PackageTypeEnum.Articles, text: $t('[[[Wybrane artykuły]]]') }
]);

const PackageTypeName = (value: PackageTypeEnum): string => PackageTypeOptions().find(p => p.value === value)?.text || PackageTypeEnum[value];

export {
    PackageTypeEnum,
    PackageTypeOptions,
    PackageTypeName
};

// ----------------------------------------------------------------------------------------------------

enum PackageLengthTypeEnum {
    Days = 'Days',
    Months = 'Months'
}

const PackageLengthTypeOptions = (): Option[] => ([
    { value: PackageLengthTypeEnum.Days, text: $t('[[[Dni]]]') },
    { value: PackageLengthTypeEnum.Months, text: $t('[[[Miesiące]]]') },
]);

const PackageLengthTypeName = (value: PackageLengthTypeEnum): string => PackageLengthTypeOptions().find(p => p.value === value)?.text || PackageLengthTypeEnum[value];

export {
    PackageLengthTypeEnum,
    PackageLengthTypeOptions,
    PackageLengthTypeName
};

// ----------------------------------------------------------------------------------------------------

enum EnvironmentTypeEnum {
    Development = 'Development',
    Production = 'Production'
}

const EnvironmentTypeOptions = (): Option[] => ([
    { value: EnvironmentTypeEnum.Development, text: $t('[[[Testowe]]]') },
    { value: EnvironmentTypeEnum.Production, text: $t('[[[Produkcyjne]]]') }
]);

const EnvironmentTypeName = (value: EnvironmentTypeEnum): string => EnvironmentTypeOptions().find(p => p.value === value)?.text || EnvironmentTypeEnum[value];

export {
    EnvironmentTypeEnum,
    EnvironmentTypeOptions,
    EnvironmentTypeName
};

// ----------------------------------------------------------------------------------------------------

enum OrderStatusEnum {
    Confirmed = 'Confirmed',
    Canceled = 'Canceled'
}

const OrderStatusOptions = (): Option[] => ([
    { value: OrderStatusEnum.Confirmed, text: $t('[[[Potwierdzone]]]') },
    { value: OrderStatusEnum.Canceled, text: $t('[[[Anulowane]]]') },
]);

const OrderStatusName = (value: OrderStatusEnum): string => OrderStatusOptions().find(p => p.value === value)?.text || OrderStatusEnum[value];

export {
    OrderStatusEnum,
    OrderStatusOptions,
    OrderStatusName
};

// ----------------------------------------------------------------------------------------------------

enum PaymentTypeEnum {
    Card = 'Card',
    Blik = 'Blik',
    PayU = 'PayU'
}

const PaymentTypeOptions = (): Option[] => ([
    { value: PaymentTypeEnum.Card, text: $t('[[[Karta]]]') },
    { value: PaymentTypeEnum.Blik, text: $t('[[[Blik]]]') },
    { value: PaymentTypeEnum.PayU, text: $t('[[[PayU]]]') }
]);

const PaymentTypeName = (value: PaymentTypeEnum): string => PaymentTypeOptions().find(p => p.value === value)?.text || PaymentTypeEnum[value];

export {
    PaymentTypeEnum,
    PaymentTypeOptions,
    PaymentTypeName
};

// ----------------------------------------------------------------------------------------------------

enum PaymentStatusEnum {
    PENDING = 'PENDING',
    WAITING_FOR_CONFIRMATION = 'WAITING_FOR_CONFIRMATION',
    COMPLETED = 'COMPLETED',
    CANCELED = 'CANCELED'
}

const PaymentStatusOptions = (): Option[] => ([
    { value: PaymentStatusEnum.PENDING, text: $t('[[[Oczekuje na płatność]]]') },
    { value: PaymentStatusEnum.WAITING_FOR_CONFIRMATION, text: $t('[[[Oczekuje na potwierdzenie]]]') },
    { value: PaymentStatusEnum.COMPLETED, text: $t('[[[Zakończone]]]') },
    { value: PaymentStatusEnum.CANCELED, text: $t('[[[Anulowane]]]') }
]);

const PaymentStatusName = (value: PaymentStatusEnum): string => PaymentStatusOptions().find(p => p.value === value)?.text || PaymentStatusEnum[value];

export {
    PaymentStatusEnum,
    PaymentStatusOptions,
    PaymentStatusName
};

// ----------------------------------------------------------------------------------------------------

enum PaymentOperatorEnum {
    PayU = 'PayU',
}

const PaymentOperatorOptions = (): Option[] => ([
    { value: PaymentOperatorEnum.PayU, text: $t('[[[PayU]]]') },
]);

const PaymentOperatorName = (value: PaymentOperatorEnum): string => PaymentOperatorOptions().find(p => p.value === value)?.text || PaymentOperatorEnum[value];

export {
    PaymentOperatorEnum,
    PaymentOperatorOptions,
    PaymentOperatorName
};

// ----------------------------------------------------------------------------------------------------
enum NotificationStage {
    Unreaded = 'Unreaded',
    Readed = 'Readed',
    Success = 'Success',
    Warning = 'Warning',
    Error = 'Error'
}

const NotificationStageOptions = (): Option<NotificationStage>[] => ([
    { value: NotificationStage.Unreaded, text: $t('[[[Nieprzeczytane]]]') },
    { value: NotificationStage.Readed, text: $t('[[[Przeczytane]]]') },
    { value: NotificationStage.Success, text: $t('[[[Potwierdzenia]]]') },
    { value: NotificationStage.Warning, text: $t('[[[Ostrzeżenia]]]') },
    { value: NotificationStage.Error, text: $t('[[[Błędy]]]') },
]);

const NotificationStageName = (value: NotificationStage): string => NotificationStageOptions().find(p => p.value === value)?.text || NotificationStage[value];

export {
    NotificationStage,
    NotificationStageOptions,
    NotificationStageName
};

// ----------------------------------------------------------------------------------------------------

enum DashboardAccessibilityType {
    Public = 'Public',
    Private = 'Private'
}

const DashboardAccessibilityTypeOptions = (): Option<DashboardAccessibilityType>[] => ([
    { value: DashboardAccessibilityType.Public, text: $t('[[[Publiczny]]]') },
    { value: DashboardAccessibilityType.Private, text: $t('[[[Prywatny]]]') }
]);

const DashboardAccessibilityTypeName = (value: DashboardAccessibilityType): string => DashboardAccessibilityTypeOptions().find(p => p.value === value)?.text;

export {
    DashboardAccessibilityType,
    DashboardAccessibilityTypeOptions,
    DashboardAccessibilityTypeName
};

// ----------------------------------------------------------------------------------------------------

enum NotificationStatus {
    Draft = 'Draft',
    Waiting = 'Waiting',
    Send = 'Send'
}

const NotificationStatusOptions = (): Option<NotificationStatus>[] => ([
    { value: NotificationStatus.Draft, text: $t('[[[Szkic]]]') },
    { value: NotificationStatus.Waiting, text: $t('[[[Oczekujące]]]') },
    { value: NotificationStatus.Send, text: $t('[[[Wysłane]]]') },
]);

const NotificationStatusName = (value: NotificationStatus): string => NotificationStatusOptions().find(p => p.value === value)?.text || NotificationStatus[value];

export {
    NotificationStatus,
    NotificationStatusOptions,
    NotificationStatusName
};

// ----------------------------------------------------------------------------------------------------

enum VariableType {
    String = 'String',
    Int32 = 'Int32',
    Int64 = 'Int64',
    Double = 'Double',
    Boolean = 'Boolean',
    DateTime = 'DateTime'
}

const VariableTypeOptions = (): Option<VariableType>[] => ([
    { value: VariableType.String, text: $t('[[[String]]]') },
    { value: VariableType.Int32, text: $t('[[[Int]]]') },
    { value: VariableType.Int64, text: $t('[[[Long]]]') },
    { value: VariableType.Double, text: $t('[[[Double]]]') },
    { value: VariableType.Boolean, text: $t('[[[Bool]]]') },
    { value: VariableType.DateTime, text: $t('[[[DateTime]]]') }
]);

const VariableTypeName = (value: VariableType): string => VariableTypeOptions().find(p => p.value === value)?.text || VariableType[value];

export {
    VariableType,
    VariableTypeOptions,
    VariableTypeName
};

// ----------------------------------------------------------------------------------------------------

enum BooleanVariable {
    True = 'true',
    False = 'false'
}

const BooleanVariableOptions = (): Option<BooleanVariable>[] => ([
    { value: BooleanVariable.True, text: $t('[[[True]]]') },
    { value: BooleanVariable.False, text: $t('[[[False]]]') }
]);

export {
    BooleanVariable,
    BooleanVariableOptions
};

// ----------------------------------------------------------------------------------------------------

enum AutomationEvents {
    DocumentChanged = 'DocumentChanged'
}

export {
    AutomationEvents
}

// ----------------------------------------------------------------------------------------------------

enum WeekDaysEnum {
    Monday = 'Monday',
    Tuesday = 'Tuesday',
    Wednesday = 'Wednesday',
    Thursday = 'Thursday',
    Friday = 'Friday',
    Saturday = 'Saturday',
    Sunday = 'Sunday'
}

const WeekDaysOptions = (): Option[] => ([
    { value: WeekDaysEnum.Monday, text: $t('[[[Poniedziałek]]]') },
    { value: WeekDaysEnum.Tuesday, text: $t('[[[Wtorek]]]') },
    { value: WeekDaysEnum.Wednesday, text: $t('[[[Środa]]]') },
    { value: WeekDaysEnum.Thursday, text: $t('[[[Czwartek]]]') },
    { value: WeekDaysEnum.Friday, text: $t('[[[Piątek]]]') },
    { value: WeekDaysEnum.Saturday, text: $t('[[[Sobota]]]') },
    { value: WeekDaysEnum.Sunday, text: $t('[[[Niedziela]]]') },
]);

const WeekDaysName = (value: WeekDaysEnum): string => WeekDaysOptions().find(p => p.value === value)?.text || WeekDaysEnum[value];

export {
    WeekDaysEnum,
    WeekDaysOptions,
    WeekDaysName
};

// ----------------------------------------------------------------------------------------------------

enum CourseModuleTypeEnum {
    Information = 'Information',
    Quiz = 'Quiz',
    Scorm = 'Scorm'
}

const CourseModuleTypeOptions = (): Option[] => ([
    { value: CourseModuleTypeEnum.Information, text: $t('[[[Aktualności]]]') },
    { value: CourseModuleTypeEnum.Quiz, text: $t('[[[Quiz]]]') },
    { value: CourseModuleTypeEnum.Scorm, text: $t('[[[Scorm]]]') },
]);

const CourseModuleTypeName = (value: CourseModuleTypeEnum): string => CourseModuleTypeOptions().find(p => p.value === value)?.text || CourseModuleTypeEnum[value];

export {
    CourseModuleTypeEnum,
    CourseModuleTypeOptions,
    CourseModuleTypeName
};


// ----------------------------------------------------------------------------------------------------

enum PriceFormatEnum {
    Gross = 'gross',
    Net = 'net',
}

export {
    PriceFormatEnum
};

// ----------------------------------------------------------------------------------------------------

enum BorderStyleEnum {
    None = 'none',
    Dashed = 'dashed',
    Dotted = 'dotted',
    Solid = 'solid'
}

const BorderStyleOptions = (): Option[] => ([
    { value: BorderStyleEnum.None, text: $t('[[[Brak]]]') },
    { value: BorderStyleEnum.Dashed, text: $t('[[[Serie kresek lub fragmentów linii]]]') },
    { value: BorderStyleEnum.Dotted, text: $t('[[[Serie kropek]]]') },
    { value: BorderStyleEnum.Solid, text: $t('[[[Pojedyńcza, prosta, ciągła linia]]]') },
]);

export {
    BorderStyleEnum,
    BorderStyleOptions
};

// ----------------------------------------------------------------------------------------------------

enum TextDecorationStyleEnum {
    Solid = 'solid',
    Double = 'double',
    Dotted = 'dotted',
    Dashed = 'dashed',
    Wavy = 'wavy'
}

const TextDecorationStyleOptions = (): Option[] => ([
    { value: TextDecorationStyleEnum.Solid, text: $t('[[[Linia ciągła]]]') },
    { value: TextDecorationStyleEnum.Double, text: $t('[[[Podwójna linia ciągła]]]') },
    { value: TextDecorationStyleEnum.Dotted, text: $t('[[[Linia kropkowana]]]') },
    { value: TextDecorationStyleEnum.Dashed, text: $t('[[[Linia kreskowana]]]') },
    { value: TextDecorationStyleEnum.Wavy, text: $t('[[[Linia falująca]]]') },
]);

export {
    TextDecorationStyleEnum,
    TextDecorationStyleOptions
};

// ----------------------------------------------------------------------------------------------------

enum CatalogOrderStatusEnum{
    New = "New",
    Accepted = "Accepted",
    Finalizing = "Finalizing",
    Sent = "Sent",
    Cancelled = "Cancelled",
    Realized = "Realized",
    ReturnedPoints_Cancelled = "ReturnedPoints_Cancelled",
    ReturnedPoints_Sent = "ReturnedPoints_Sent",
    Finished = 'Finished'
}

const CatalogOrderStatusOptions = (): Option[] => ([
    { value: CatalogOrderStatusEnum.New, text: $t('[[[Nowe]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.Accepted, text: $t('[[[Zamówienie zaakceptowane]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.Finalizing, text: $t('[[[Kompletowanie zamówienia]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.Sent, text: $t('[[[Wysłane]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.Cancelled, text: $t('[[[Anulowane]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.Realized, text: $t('[[[Realizowane]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.ReturnedPoints_Cancelled, text: $t('[[[Anulowane (zwrot punktów)]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.ReturnedPoints_Sent, text: $t('[[[Wysłane (zwrot punktów)]]]'), icon: '' },
    { value: CatalogOrderStatusEnum.Finished, text: $t('[[[Zakończone]]]'), icon: '' },
]);

const CatalogOrderStatusName = (value: CatalogOrderStatusEnum): string => CatalogOrderStatusOptions().find(p => p.value === value)?.text || CatalogOrderStatusEnum[value];

export {
    CatalogOrderStatusEnum,
    CatalogOrderStatusOptions,
    CatalogOrderStatusName
};


// ----------------------------------------------------------------------------------------------------

enum CompanySizeEnum {
    JDG = 'JDG',
    UpToFive = 'UpToFive',
    FiveToTen = 'FiveToTen',
    MoreThanTen = 'MoreThanTen'
}

const CompanySizeOptions = (): Option[] => ([
    { value: CompanySizeEnum.JDG, text: $t('[[[JDG]]]') },
    { value: CompanySizeEnum.UpToFive, text: $t('[[[Do 5 osób]]]') },
    { value: CompanySizeEnum.FiveToTen, text: $t('[[[5 - 10 osób]]]') },
    { value: CompanySizeEnum.MoreThanTen, text: $t('[[[Powyżej 10 osób]]]') }
]);

const CompanySizeName = (value: CompanySizeEnum): string => CompanySizeOptions().find(p => p.value === value)?.text || CompanySizeEnum[value];

export {
    CompanySizeEnum,
    CompanySizeOptions,
    CompanySizeName
};


// ----------------------------------------------------------------------------------------------------

enum SettlementFormEnum {
    Private = 'Private',
    Business = 'Business'
}

const SettlementFormOptions = (): Option[] => ([
    { value: SettlementFormEnum.Private, text: $t('[[[Osobisty przychód]]]') },
    { value: SettlementFormEnum.Business, text: $t('[[[Działalność gospodarcza]]]') }
]);

export {
    SettlementFormEnum,
    SettlementFormOptions
};

// ----------------------------------------------------------------------------------------------------

enum ContactFormEntryStatusEnum {
    New = 'New',
    Rejected = 'Rejected',
    Resolved = 'Resolved'
}

const ContactFormEntryStatusOptions = (): Option[] => ([
    { value: ContactFormEntryStatusEnum.New, text: $t('[[[Nowe]]]') },
    { value: ContactFormEntryStatusEnum.Rejected, text: $t('[[[Odrzucone]]]') },
    { value: ContactFormEntryStatusEnum.Resolved, text: $t('[[[Rozwiązane]]]') }
]);

const ContactFormEntryStatusName = (value: ContactFormEntryStatusEnum): string => ContactFormEntryStatusOptions().find(p => p.value === value)?.text || ContactFormEntryStatusEnum[value];

export {
    ContactFormEntryStatusEnum,
    ContactFormEntryStatusOptions,
    ContactFormEntryStatusName
};

// ----------------------------------------------------------------------------------------------------

enum AnalyticsDimensionEnum {
    Country = 'country',
    Region = 'region',
    City = 'city',
    Continent = 'continent',
    DeviceCategory = 'deviceCategory',
    Browser = 'browser',
    OperatingSystem = 'operatingSystem',
    Date = 'date',
    Hour = 'hour',
    DayOfWeek = 'dayOfWeek',
    Week = 'week'
}

const AnalyticsDimensionOptions = (): Option[] => ([
    { value: AnalyticsDimensionEnum.Country, text: $t('[[[Kraj]]]') },
    { value: AnalyticsDimensionEnum.Region, text: $t('[[[Region]]]') },
    { value: AnalyticsDimensionEnum.City, text: $t('[[[Miasto]]]') },
    { value: AnalyticsDimensionEnum.Continent, text: $t('[[[Kontynent]]]') },
    { value: AnalyticsDimensionEnum.DeviceCategory, text: $t('[[[Kategoria urządzenia]]]') },
    { value: AnalyticsDimensionEnum.Browser, text: $t('[[[Przeglądarka]]]') },
    { value: AnalyticsDimensionEnum.OperatingSystem, text: $t('[[[System operacyjny]]]') },
    { value: AnalyticsDimensionEnum.Date, text: $t('[[[Data]]]') },
    { value: AnalyticsDimensionEnum.Hour, text: $t('[[[Godzina]]]') },
    { value: AnalyticsDimensionEnum.DayOfWeek, text: $t('[[[Dzień tygodnia]]]') },
    { value: AnalyticsDimensionEnum.Week, text: $t('[[[Tydzień]]]') },
]);

export {
    AnalyticsDimensionEnum,
    AnalyticsDimensionOptions
};

// ----------------------------------------------------------------------------------------------------

enum UserStatusEnum {
    Waiting = 'Waiting',
    Active = 'Active',
    Inactive = 'Inactive'
}

const UserStatusOptions = (): Option[] => ([
    { value: UserStatusEnum.Waiting, text: $t('[[[Oczekuje na weryfikację]]]') },
    { value: UserStatusEnum.Active, text: $t('[[[Aktywny]]]') },
    { value: UserStatusEnum.Inactive, text: $t('[[[Nieaktywny]]]') }
]);

const UserStatusName = (value: UserStatusEnum): string => UserStatusOptions().find(p => p.value === value)?.text || UserStatusEnum[value];
const UserStatusStyle = (classTemplate: string, status: UserStatusEnum): Record<string, boolean> =>
{
    const classes: Record<string, boolean> = {};
    const re = new RegExp('\\{0\\}', 'g');

    classes[classTemplate.replace(re, 'warning')] = status === UserStatusEnum.Waiting;
    classes[classTemplate.replace(re, 'success')] = status === UserStatusEnum.Active;
    classes[classTemplate.replace(re, 'danger')] = status === UserStatusEnum.Inactive;

    return classes;
};

export {
    UserStatusEnum,
    UserStatusOptions,
    UserStatusName,
    UserStatusStyle
};

// ----------------------------------------------------------------------------------------------------

enum CarrierTypeEnum {
    Courier = 'Courier',
    ParcelMachine = 'ParcelMachine',
    PersonalCllection = 'PersonalCllection'
}

const CarrierTypeOptions = (): Option[] => ([
    { value: CarrierTypeEnum.Courier, text: $t('[[[Kurier]]]') },
    { value: CarrierTypeEnum.ParcelMachine, text: $t('[[[Automat paczkowy]]]') },
    { value: CarrierTypeEnum.PersonalCllection, text: $t('[[[Odbiór osobisty]]]') }
]);

export {
    CarrierTypeEnum,
    CarrierTypeOptions
};

// ----------------------------------------------------------------------------------------------------

enum StoreOrderStatusEnum {
    New = 'New',
    PaymentPending = 'PaymentPending',
    Confirmed = 'Confirmed',
    InProgress = 'InProgress',
    Sent = 'Sent',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    Return = 'Return',
    Failed = 'Failed',
    Suspended = 'Suspended'
}

const StoreOrderStatusOptions = (): Option[] => ([
    { value: StoreOrderStatusEnum.New, text: $t('[[[Nowe]]]') },
    { value: StoreOrderStatusEnum.PaymentPending, text: $t('[[[Oczekuje na płatność]]]') },
    { value: StoreOrderStatusEnum.Confirmed, text: $t('[[[Potwierdzone]]]') },
    { value: StoreOrderStatusEnum.InProgress, text: $t('[[[W trakcie realizacji]]]') },
    { value: StoreOrderStatusEnum.Sent, text: $t('[[[Wysłane]]]') },
    { value: StoreOrderStatusEnum.Completed, text: $t('[[[Zrealizowane]]]') },
    { value: StoreOrderStatusEnum.Suspended, text: $t('[[[Wstrzymane]]]') },
    { value: StoreOrderStatusEnum.Failed, text: $t('[[[Nieudane]]]') },
    { value: StoreOrderStatusEnum.Return, text: $t('[[[Zwrot]]]') },
    { value: StoreOrderStatusEnum.Cancelled, text: $t('[[[Anulowane]]]') }
]);

const StoreOrderStatusName = (value: StoreOrderStatusEnum): string => StoreOrderStatusOptions().find(p => p.value === value)?.text || StoreOrderStatusEnum[value];

export {
    StoreOrderStatusEnum,
    StoreOrderStatusOptions,
    StoreOrderStatusName
};

// ----------------------------------------------------------------------------------------------------

enum StorePaymentStatusEnum {
    Pending = 'Pending',
    Completed = 'Completed',
    Canceled = 'Canceled',
    NotStarted = 'NotStarted',
    Failed = 'Failed',
    NotConfirmed = 'NotConfirmed'
}

const StorePaymentStatusOptions = (): Option[] => ([
    { value: StorePaymentStatusEnum.Pending, text: $t('[[[Oczekuje na płatność]]]') },
    { value: StorePaymentStatusEnum.NotStarted, text: $t('[[[Nieopłacone]]]') },
    { value: StorePaymentStatusEnum.NotConfirmed, text: $t('[[[Niepotwierdzona]]]') },
    { value: StorePaymentStatusEnum.Completed, text: $t('[[[Opłacone]]]') },
    { value: StorePaymentStatusEnum.Failed, text: $t('[[[Błąd płatności]]]') },
    { value: StorePaymentStatusEnum.Canceled, text: $t('[[[Anulowane]]]') }
]);

const StorePaymentStatusName = (value: StorePaymentStatusEnum): string => StorePaymentStatusOptions().find(p => p.value === value)?.text || StorePaymentStatusEnum[value];

export {
    StorePaymentStatusEnum,
    StorePaymentStatusOptions,
    StorePaymentStatusName
};

// ----------------------------------------------------------------------------------------------------

enum StoreDeliveryStatusEnum {
    AwaitingShipment = 'AwaitingShipment',
    Preparation = 'Preparation',
    Sent = 'Sent',
    Transit = 'Transit',
    Delivered = 'Delivered',
    DeliveryAttemptFailed = 'DeliveryAttemptFailed',
    PickedUpAtLocation = 'PickedUpAtLocation',
    Returned = 'Returned',
    Canceled = 'Canceled'
}

const StoreDeliveryStatusOptions = (): Option[] => ([
    { value: StoreDeliveryStatusEnum.AwaitingShipment, text: $t('[[[Oczekuje na wysyłkę]]]') },
    { value: StoreDeliveryStatusEnum.Preparation, text: $t('[[[W trakcie przygotowania]]]') },
    { value: StoreDeliveryStatusEnum.Sent, text: $t('[[[Wysłane]]]') },
    { value: StoreDeliveryStatusEnum.Transit , text: $t('[[[W drodze]]]') },
    { value: StoreDeliveryStatusEnum.Delivered, text: $t('[[[Dostarczone]]]') },
    { value: StoreDeliveryStatusEnum.PickedUpAtLocation, text: $t('[[[Odebrane w punkcie]]]') },
    { value: StoreDeliveryStatusEnum.DeliveryAttemptFailed, text: $t('[[[Nieudana próba doręczenia]]]') },
    { value: StoreDeliveryStatusEnum.Returned, text: $t('[[[Zwrot do nadawcy]]]') },
    { value: StoreDeliveryStatusEnum.Canceled, text: $t('[[[Anulowane]]]') }
]);

const StoreDeliveryStatusName = (value: StoreDeliveryStatusEnum): string => StoreDeliveryStatusOptions().find(p => p.value === value)?.text || StoreDeliveryStatusEnum[value];

export {
    StoreDeliveryStatusEnum,
    StoreDeliveryStatusOptions,
    StoreDeliveryStatusName
};

// ----------------------------------------------------------------------------------------------------

enum StoreOrderStatusTypeEnum {
    Order = 'Order',
    Payment = 'Payment',
    Delivery = 'Delivery'
}

const StoreOrderStatusTypeOptions = (): Option[] => ([
    { value: StoreOrderStatusTypeEnum.Order, text: $t('[[[Ogólny]]]') },
    { value: StoreOrderStatusTypeEnum.Payment, text: $t('[[[Płatność]]]') },
    { value: StoreOrderStatusTypeEnum.Delivery, text: $t('[[[Dostawa]]]') },
]);

const StoreOrderStatusTypeName = (value: StoreOrderStatusTypeEnum): string => StoreOrderStatusTypeOptions().find(p => p.value === value)?.text || StoreOrderStatusTypeEnum[value];

export {
    StoreOrderStatusTypeEnum,
    StoreOrderStatusTypeOptions,
    StoreOrderStatusTypeName
};

// ----------------------------------------------------------------------------------------------------

enum StoreOrderParcelPointCarriersEnum {
    ParcelMachine = 'ParcelMachine'
}

export {
    StoreOrderParcelPointCarriersEnum
};

